import {
  Box,
  VStack,
  Text,
  Heading,
  Badge,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Alert,
} from '@chakra-ui/react';
// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Import required modules
import { Navigation, Pagination, Mousewheel, Keyboard } from 'swiper/modules';
import { memo, useState } from 'react';
import { tracking } from '../utils/tracking';
import WhyThisWorksInfo from './WhyThisWorksInfo';
import InfoBadge from './InfoBadge';

const ProductCard = memo(({ product, isSingleProduct = false }) => {
  const [isInfoPopupOpen, setIsInfoPopupOpen] = useState(false);
  const [isWhyThisWorksOpen, setIsWhyThisWorksOpen] = useState(false);
  const extractStoreName = (url) => {
    try {
      const domain = new URL(url).hostname;
      return domain.replace('www.', '');
    } catch {
      return 'Store';
    }
  };

  const handleClick = () => {
    // Only open link if no popup is open
    if (!isInfoPopupOpen && !isWhyThisWorksOpen) {
      // Track product click
      tracking.productClicked(product.product_id || product.id, 'recommendation');
      tracking.productViewed(product.product_id || product.id, {
        title: product.title,
        price: product.price,
        store: extractStoreName(product.product_url)
      });
      
      window.open(product.product_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      overflow="hidden"
      bg="white"
      height="auto"
      maxHeight="60vh"
      width={isSingleProduct ? "90%" : "90%"}
      flexShrink={0}
    >
      <Box 
        position="relative"
        cursor="pointer"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`View ${product.title}`}
        transition="all 0.2s ease-in-out"
        _hover={{
          transform: "scale(1.05)"
        }}
      >
        <img
          src={product.image_url}
          alt={product.title}
          style={{
            width: "100%",
            height: "52vh",
            objectFit: "cover",
            borderBottom: "1px solid #8d8c8cff"
          }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
        
        {/* Recommendation Tag - Bottom left overlay */}
        {product.recommendation_tag && (
          <Badge
            position="absolute"
            bottom={2}
            left={2}
            colorScheme="purple"
            variant="solid"
            size="sm"
            borderRadius="full"
            fontSize="xs"
            fontWeight="medium"
            zIndex={10}
            onClick={(e) => e.stopPropagation()}
            style={{
              boxShadow: "0 0.125rem 0.375rem rgba(0, 0, 0, 0.4)",
              backgroundColor: "rgba(0, 0, 0, 0.8)"
            }}
          >
            {product.recommendation_tag}
          </Badge>
        )}
        
        {/* Info Badge - Top right overlay */}
        <Box
          position="absolute"
          top={2}
          right={2}
          zIndex={10}
        >
          <InfoBadge 
            price={product.max_price}
            website={extractStoreName(product.product_url)}
            productUrl={product.product_url}
            onPopupStateChange={setIsInfoPopupOpen}
          />
        </Box>
        
        {/* Why This Works - Bottom right overlay */}
        <Box
          position="absolute"
          bottom={2}
          right={2}
          zIndex={10}
        >
          <WhyThisWorksInfo 
            whyThisWorks={product.why_this_works} 
            onPopupStateChange={setIsWhyThisWorksOpen}
          />
        </Box>
      </Box>
      
      <VStack spacing={1} px={2} pt={2} pb={1} alignItems="flex-start" height="auto" borderBottom="1px solid #8d8c8cff">
        
        <Text
          fontSize="sm"
          color="gray.600"
          noOfLines={1}
          lineHeight="short"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          width="100%"
        >
          {product.title}
        </Text>
      </VStack>
    </Box>
  );
});

ProductCard.displayName = 'ProductCard';


const RecommendationCard = memo(({ search, isLoading = false }) => {
  const products = search.products || [];
  const [currentSlide, setCurrentSlide] = useState(1);
  const totalSlides = products.length;

  if (isLoading) {
    return (
      <Box
        borderWidth="1px"
        borderRadius="lg"
        shadow="sm"
        backgroundColor="white"
        padding={6}
        marginBottom={4}
      >
        <SkeletonText mt="4" noOfLines={2} spacing="4" skeletonHeight="2" />
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} marginTop={4}>
          {[1, 2, 3].map((i) => (
            <Box key={i} borderWidth="1px" borderRadius="md" overflow="hidden">
              <Skeleton height="200px" />
              <VStack spacing={3} padding={4} alignItems="flex-start">
                <SkeletonText noOfLines={1} skeletonHeight="4" width="60px" />
                <SkeletonText noOfLines={1} skeletonHeight="3" width="80px" />
                <SkeletonText noOfLines={2} skeletonHeight="3" />
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  if (search.error) {
    return (
      <Alert status="warning" borderRadius="md" marginBottom={4}>
        ⚠️ {search.error}
      </Alert>
    );
  }

  return (
    <>
      <Heading
        as="h3"
        size="md"
        color="gray.800"
        marginBottom={4}
        textAlign="center"
      >
        {search.recommendation}
      </Heading>
      
      {/* Single Product - Simple Container */}
      {products.length === 1 ? (
        <div style={{ position: "relative", width: "100%", maxWidth: "400px", padding: "0", margin: "0 auto", display: "flex", justifyContent: "center", overflow: "hidden" }}>
          <ProductCard product={products[0]} isSingleProduct={true} />
        </div>
      ) : (
        /* Multiple Products - SwiperJS Carousel */
        <div style={{ position: "relative", width: "100%", maxWidth: "min(400px, 80vw)", padding: "0", overflow: "hidden", margin: "0 auto" }}>
          <Swiper
            modules={[Navigation, Pagination, Mousewheel, Keyboard]}
            spaceBetween={0}
            slidesPerView={1}
            navigation={false}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            mousewheel={true}
            keyboard={true}
            loop={products.length > 1}
            cssMode={true}
            onSlideChange={(swiper) => setCurrentSlide(swiper.realIndex + 1)}
            onSwiper={(swiper) => setCurrentSlide(swiper.realIndex + 1)}
            style={{
              '--swiper-navigation-color': '#3182ce',
              '--swiper-pagination-color': '#3182ce',
              '--swiper-pagination-bottom': '5px',
              width: '100%',
              height: '58vh',
              marginLeft: '0',
            }}
          >
            {products.map((product, index) => (
              <SwiperSlide key={index} style={{ display: 'flex', justifyContent: 'center' }}>
                <ProductCard product={product} />
              </SwiperSlide>
            ))}
          </Swiper>
          
          {/* Custom Pagination Indicator */}
          {totalSlides > 1 && (
            <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
              <div style={{ fontSize: "14px", color: "#6b7280", fontWeight: "500" }}>
                {currentSlide}/{totalSlides}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
});

RecommendationCard.displayName = 'RecommendationCard';

const ProductRecommendations = ({ 
  productSearches = [], 
  complementaryItems = [],
  isLoading = false 
}) => {
  return (
    <Box bg="transparent" py={0} display="flex" justifyContent="center" width="100%">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.025rem', width: '100%' }}>

        {complementaryItems && complementaryItems.length > 0 && (
          <Box
            width="100%"
            borderWidth="1px"
            borderRadius="md"
            borderColor="gray.200"
            backgroundColor="gray.50"
            padding={4}
          >
            <Heading
              as="h4"
              size="sm"
              color="gray.700"
              marginBottom={3}
            >
              💡 Complementary Items
            </Heading>
            <VStack spacing={1} alignItems="flex-start">
              {complementaryItems.map((item, index) => (
                <Text key={index} fontSize="xs" color="gray.600">
                  • {item}
                </Text>
              ))}
            </VStack>
          </Box>
        )}

        {isLoading ? (
          <>
            {[1, 2, 3].map((i) => (
              <RecommendationCard key={i} search={{}} isLoading={true} />
            ))}
          </>
        ) : (
          <>
            {productSearches.map((search, index) => (
              <RecommendationCard 
                key={index} 
                search={search} 
                isLoading={false}
              />
            ))}
          </>
        )}

      </div>
    </Box>
  );
};

export default ProductRecommendations;